// src/pages/ContactUsPage.js
"use client";

import React from "react";
import { Helmet } from "react-helmet-async";


/* ---------- Small UI pieces ---------- */

const SectionWrap = ({ children }) => (
  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">{children}</div>
);

const Button = ({ children, as = "a", href = "#", className = "" }) => {
  const base =
    "inline-flex items-center justify-center rounded-xl h-11 px-5 text-sm font-semibold transition";
  const fill = "bg-blue-600 text-white hover:bg-blue-700";
  return as === "button" ? (
    <button className={`${base} ${fill} ${className}`}>{children}</button>
  ) : (
    <a href={href} className={`${base} ${fill} ${className}`}>
      {children}
    </a>
  );
};

const GhostButton = ({ children, href }) => (
  <a
    href={href}
    className="inline-flex items-center justify-center rounded-xl h-11 px-5 text-sm font-semibold border border-gray-300 text-gray-800 hover:bg-gray-50"
  >
    {children}
  </a>
);

const InfoCard = ({ title, lines = [], icon }) => (
  <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
    <div className="flex items-start gap-3">
      <div className="text-blue-700 mt-1">{icon}</div>
      <div>
        <h3 className="heading-serif text-lg font-bold text-gray-900">{title}</h3>
        <ul className="mt-2 space-y-1">
          {lines.map((t, i) => (
            <li key={i} className="text-sm text-gray-700">
              {t}
            </li>
          ))}
        </ul>
      </div>
    </div>
  </div>
);

/* ---------- Page ---------- */

const ContactUsPage = () => {
  return (
    <div className="flex flex-col min-h-screen bg-white">
      <Helmet>
        <title>Contact Us - Maoq Digital Inc.</title>
        <link rel="icon" type="image/x-icon" href="/favicon.ico" />
      </Helmet>

      <main className="flex-grow">
        {/* HERO */}
        <section
          className="relative text-center text-white"
          style={{
            backgroundImage:
              'linear-gradient(rgba(6,35,70,.86), rgba(6,35,70,.9)), url("https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?q=80&w=1920&auto=format&fit=crop")',
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          <SectionWrap>
            <div className="py-16">
              <h1 className="heading-serif text-3xl sm:text-4xl md:text-5xl font-bold">
                Contact Us
              </h1>
              <p className="mt-3 max-w-2xl mx-auto text-sm sm:text-base text-gray-100">
                Canada-based advisory for hotels, plazas, and land. Reach out any time - we keep it simple and private.
              </p>
              <div className="mt-6 flex flex-wrap justify-center gap-3">
                <Button href="mailto:info@maoqdigital.com">
                  Email info@maoqdigital.com
                </Button>
                <Button href="/listings">View Listings</Button>
              </div>
            </div>
          </SectionWrap>
        </section>

        {/* INFO TILES */}
        <SectionWrap>
          <div className="py-10">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              <InfoCard
                title="Email"
                lines={["info@maoqdigital.com", "We reply within 1–2 business days."]}
                icon={
                  <svg width="26" height="26" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20 4H4a2 2 0 0 0-2 2v1l10 6 10-6V6a2 2 0 0 0-2-2Zm0 5-8 4.8L4 9v9a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9Z" />
                  </svg>
                }
              />
    
              <InfoCard
                title="Hours"
                lines={["Mon–Fri: 9am–6pm ET", "By appointment only"]}
                icon={
                  <svg width="26" height="26" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19 3H5a2 2 0 0 0-2 2v12l4-4h12a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2Zm0 15H5V10h14v9Zm-6-8h-2v5h5v-2h-3V11Z" />
                  </svg>
                }
              />
            </div>
          </div>
        </SectionWrap>

        {/* QUICK LINKS STRIP */}
        <SectionWrap>
          <div className="py-2">
            <div className="rounded-2xl border border-gray-200 bg-gray-50 p-6 sm:p-8 shadow-sm text-center">
              <h2 className="heading-serif text-2xl font-bold text-gray-900">How to reach us</h2>
              <p className="mt-2 text-sm text-gray-700">
                Prefer email? Click below — or drop by with an appointment.
              </p>
              <div className="mt-5 flex flex-wrap gap-3 justify-center">
                <Button href="mailto:info@maoqdigital.com">Email us</Button>
                <GhostButton href="/listings">Browse listings</GhostButton>
              </div>
            </div>
          </div>
        </SectionWrap>

          {/* FOOTER SPACER */}
        <div className="py-12" />
      </main>
    </div>
  );
};

export default ContactUsPage;
